import type { MondayBoardContext } from "@/services/monday-service";
import { mondayService } from "@/services/monday-service";
import { isOAuthTokenAvailable } from "@/services/oauth-service";
import type { AutomationActions } from "@/types/automation";
import type { ActionResult, PollResult } from "@/types/common";

import { buildQueryString, fuseBaseServiceUrl } from "./endpoints-url";

// Interfaces that specifies the CLI service made of a single method telling how to run a command.
export interface RunCliService {
  runActions: (
    actions: AutomationActions[],
    requestId: string,
    context: MondayBoardContext,
    sessionToken: string
  ) => Promise<Response>;
  pollCommand: (requestId: string, context: MondayBoardContext, sessionToken: string) => Promise<PollResult>;
}

export const cliService: RunCliService = {
  runActions,
  pollCommand,
};

export type RunResult = string;

export async function runActions(
  actions: AutomationActions[],
  requestId: string,
  context: MondayBoardContext,
  sessionToken: string
): Promise<Response> {
  const fuseBaseUrl = fuseBaseServiceUrl();
  const appKey = import.meta.env.VITE_MONDAY_APP_KEY as string;
  let response = await fetch(`${fuseBaseUrl}/sharedapi/automation/1.1/run?productType=monday`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": sessionToken,
      "appkey": appKey,
    },
    body: JSON.stringify({
      automation: {
        resultsUrl: requestId,
        actions: actions,
      },
      context,
    }),
  });

  // Retry logic for 403 status from auth_lambda
  if (response.status === 403) {
    console.log("Retrying due to 403 status...");
    const appKey = import.meta.env.VITE_MONDAY_APP_KEY as string;
    const recentSessionToken = await mondayService.getSessionToken();
    await isOAuthTokenAvailable(recentSessionToken);
    response = await fetch(`${fuseBaseUrl}/sharedapi/automation/1.1/run?productType=monday`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": recentSessionToken,
        "appkey": appKey,
      },
      body: JSON.stringify({
        automation: {
          resultsUrl: requestId,
          actions: actions,
        },
        context,
      }),
    });
  }

  if (!response.ok) {
    console.error(response.statusText || response.status);
    throw Error(response.statusText || response.status.toString());
  }

  return response;
}

async function pollCommand(requestId: string, context: MondayBoardContext, sessionToken: string): Promise<PollResult> {
  const fuseBaseUrl = fuseBaseServiceUrl();
  const queryString = buildQueryString({
    request_id: requestId,
    // https://appfire.atlassian.net/browse/CONCOM-3101
    productType: "monday",
  });
  const resultsUrl = `${fuseBaseUrl}/api/automationresults?${queryString}`;
  const appKey = import.meta.env.VITE_MONDAY_APP_KEY as string;
  const response = await fetch(resultsUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": sessionToken,
      "appkey": appKey,
    },
  });
  // TODO: Generally this needs much more work...
  // The observed behavior of ac-automation does not match that expected from documentation or conversation.
  // The following is based observed behavior.
  if (response.ok) {
    // TODO: Better marshalling solution / get rid of type assertion
    const data = (await response.json()) as ActionResult;
    // A null exit code or lack of results generally means the results are pending
    if (!data.exitCode || !data.results) {
      return { retry: true, success: false, duration: data.duration, exitCode: data.exitCode, results: data.results };
    }
    // An exit code & results generally means processing is complete
    else if (data.exitCode && data.results) {
      return { retry: false, success: true, duration: data.duration, exitCode: data.exitCode, results: data.results };
    }
    // TODO: Can this happen?
    else {
      return { retry: true };
    }
  } else if (response.status === 404 || response.status === 403) {
    // A 404 status also generally means the results are pending
    return { retry: true };
  } else {
    // Otherwise, the response indicates some kind of error, probably...
    return { retry: false, success: false, failureReason: response.statusText };
  }
}
